import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Search, Building2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import ConsumerLayout from '@/components/ConsumerLayout';
import api from '@/lib/api';

const NEIGHBORHOOD_NAMES = {
  'rino': 'RiNo (River North)', 'cherry-creek': 'Cherry Creek', 'lodo': 'LoDo (Lower Downtown)',
  'sobo': 'South Broadway (SoBo)', 'highland-lohi': 'Highland / LoHi',
  'capitol-hill': 'Capitol Hill', 'tennyson': 'Tennyson Street',
};

export default function VenueBrowseByLocation() {
  const { state, city, neighborhood } = useParams();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const endpoint = neighborhood
          ? `/venues/browse/${state}/${city}/${neighborhood}`
          : `/venues/browse/${state}/${city}`;
        const res = await api.get(endpoint);
        setVenues(res.data.venues || []);
      } catch { setVenues([]); }
      finally { setLoading(false); }
    };
    fetchVenues();
  }, [state, city, neighborhood]);

  const filtered = venues.filter(v =>
    !search || v.name?.toLowerCase().includes(search.toLowerCase())
  );

  const title = neighborhood
    ? `${NEIGHBORHOOD_NAMES[neighborhood] || neighborhood} Venues`
    : `${city?.charAt(0).toUpperCase() + city?.slice(1)} Venues`;

  const generateSlug = (name) => name?.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').trim() || '';

  return (
    <ConsumerLayout darkTheme>
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-6">
          <Link to="/venues" className="hover:text-white">Venues</Link>
          <span>/</span>
          <Link to={`/venues/${state}`} className="hover:text-white">{state?.toUpperCase()}</Link>
          <span>/</span>
          <Link to={`/venues/${state}/${city}`} className="hover:text-white">{city?.charAt(0).toUpperCase() + city?.slice(1)}</Link>
          {neighborhood && <>
            <span>/</span>
            <span className="text-white">{NEIGHBORHOOD_NAMES[neighborhood] || neighborhood}</span>
          </>}
        </div>

        <h1 className="text-4xl font-bold text-white mb-2">{title}</h1>
        <p className="text-slate-400 mb-8">{filtered.length} {filtered.length === 1 ? 'venue' : 'venues'} found</p>

        <div className="relative mb-8">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <Input placeholder="Search venues..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-12 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 h-12" />
        </div>

        {loading ? (
          <div className="text-center py-20 text-slate-400">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No venues found in this area yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map(venue => (
              <Link key={venue.id} to={`/venues/${(venue.state || state || 'co').toLowerCase()}/${(venue.city || city || 'denver').toLowerCase()}/${venue.neighborhood || 'rino'}/${venue.slug || generateSlug(venue.name)}`}>
                <Card className="bg-slate-800/50 border-slate-700 hover:border-indigo-500 transition-all overflow-hidden group">
                  <div className="aspect-video bg-slate-700 overflow-hidden">
                    {venue.images?.[0] ? (
                      <img src={venue.images[0]} alt={venue.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Building2 className="w-12 h-12 text-slate-600" /></div>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-white text-lg">{venue.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="secondary" className="text-xs">{venue.venue_type || venue.category}</Badge>
                      {venue.price_level && <span className="text-sm text-slate-400">{venue.price_level}</span>}
                    </div>
                    {venue.address && <p className="text-sm text-slate-400 mt-2 flex items-center gap-1"><MapPin className="w-3 h-3" /> {venue.address}</p>}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </ConsumerLayout>
  );
}
